import { useState } from 'react';
import { LogOut } from 'lucide-react';
import { getFirebaseAuth } from '@heyroot/auth-shared';
import { useAdminAuth } from '../lib/auth-context';
import { clearRecents } from '../lib/palette-recents';

// Sidebar footer identity card (spec §7.6). Replaces the header avatar
// dropdown: V1 puts identity at the bottom of the nav rail, and having two
// sign-out affordances in one shell is worse than either alone.
//
// admin.admin_users has no name column, so this shows initials + email + role
// rather than V1's display name.
function deriveInitials(email: string): string {
  const local = email.split('@')[0] ?? '';
  if (!local) return '?';
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  if (local.length <= 3) return local.toUpperCase();
  return local[0]!.toUpperCase();
}

export function SidebarFooter() {
  const { email, activeRole } = useAdminAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState(false);

  if (!email) return null;

  async function handleSignOut() {
    setSigningOut(true);
    setError(false);
    try {
      const { signOut } = await import('firebase/auth');
      await signOut(getFirebaseAuth());
    } catch (err) {
      console.error('Firebase signOut failed', err);
      setError(true);
      setSigningOut(false);
      return;
    }
    clearRecents();
    window.location.assign('/login');
  }

  return (
    <div className="mt-auto border-t border-border pt-2 px-1">
      <div className="flex items-center gap-2 px-1.5 py-1.5">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-primary text-caption text-brand-primary-fg"
        >
          {deriveInitials(email)}
        </span>
        <span className="min-w-0 flex flex-col">
          <span className="truncate text-body-sm text-primary">{email}</span>
          <span className="truncate text-caption text-muted">{activeRole}</span>
        </span>
      </div>
      {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: compact shell chrome row, not a standalone CTA Button */}
      <button
        type="button"
        onClick={() => void handleSignOut()}
        disabled={signingOut}
        className="flex w-full items-center gap-2 rounded-control px-2.5 py-1.5 text-left text-body-sm text-secondary transition-colors duration-[var(--duration-fast)] hover:bg-surface-sunken hover:text-primary disabled:opacity-50"
      >
        <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
        {signingOut ? 'Signing out…' : 'Sign Out'}
      </button>
      {error && (
        <div role="alert" className="px-2.5 py-1 text-caption text-danger-text">
          Sign out failed — try again.
        </div>
      )}
    </div>
  );
}
